import * as React from 'react';
import { connect } from 'react-redux';
import MatchList, { MatchListAddMoreAnchor } from '../../../match-list/match-list';
import { AppState } from '@om-fe/core/core/types';
import { areTopBetsMatchListTopicPropsEqual, getTopBetsMatchListSliceName } from './top-bets-list.utils';
import {
    addMoreTopBetsMatchList,
    getTopBetsMatchGroupsOverviewList,
    removeTopBetsMatchGroupsOverviewList,
    updateTopBetsMatchGroupsOverviewList,
} from './top-bets-list.actions';
import {
    TopBetsMatchListStoreModel,
} from './top-bets-list.constants';
import { compose } from 'redux';
import preLoader from '@om-fe/core/core/bootstrap/preload';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import withRouting from '@om-fe/core/core/routing/with-routing';
import { EMRoutingContextInterface } from '@om-fe/core/core/routing/utils';
import { MatchListCommon } from '@om-fe/core/components/match-list/utils/match-list-models';
import { DEFAULT_DISPLAYED_NUMBER_OF_MATCHES, MatchListItemLayout } from '../../../match-list/utils/match-list.constants';
import MatchListSkeleton from '@om-fe/core/components/match-list/match-list-skeleton';
import { DisciplineLiveStatus } from '@om-fe/models';
import getConfig, { OMFE2ConfigModel, MarketsHeaderOptions } from '@om-fe/core/utils/config';
import difference from 'lodash/difference';
import { FilteredMarketGroupsOverview } from '../../../match-list/utils/match-list-models';
import { PlatformTypes } from '@om-fe/utils';
import { TimeRangeType } from '../../utils/top-bets.constants';
import { getVirtualStateExtraParams } from '@om-fe/core/core/routing/custom-routes/custom-routes.utils';

export interface TopBetsMatchListPublicProps extends MatchListCommon {
    readonly instanceId: string;
    readonly disciplineId: string;
    readonly redirectAnchor?: MatchListAddMoreAnchor;
    readonly itemLayout?: MatchListItemLayout;
    readonly limitNumberOfEvents: number | undefined;
    readonly liveStatus: DisciplineLiveStatus;
    readonly mainMarketsLimit?: number;
    readonly marketsHeaderOptions: MarketsHeaderOptions;
    readonly showMatchListHeader: boolean;
    readonly showMarketInMatchListGroupHeader: boolean;
    readonly groupOverview?: string;
    readonly hasMarketSelector?: boolean;
    readonly selectGroupsOverview?: (item: string) => void;
    readonly filteredMarketGroupsOverviewList?: string[];
    readonly filteredMarketGroupsOverview?: Record<string, FilteredMarketGroupsOverview>;
    readonly isHasDrawer?: boolean;
    readonly deviceType?: PlatformTypes;
    readonly timeRange: string;
    readonly locationId?: string;
    readonly categoryId?: string;
    readonly isTopBets?: boolean;
    readonly isCategory?: boolean;
    readonly timeRangeType?: TimeRangeType;
    readonly timeRangeValue?: string;
}

interface TopBetsMatchListStoreProps {
    readonly sliceKey: string;
    readonly limit: number;
    readonly totalRecords: number;
    readonly ready: boolean;
}

interface TopBetsMatchListDispatchProps {
    readonly addMore: (instanceId: string, limitNumberOfEvents: number) => void;
}

interface MatchLimitStorageData {
    readonly matchLimit?: number;
}

type TopBetsMatchListProps = TopBetsMatchListPublicProps
    & TopBetsMatchListStoreProps
    & TopBetsMatchListDispatchProps
    & I18nContextInterface
    & EMRoutingContextInterface;

function TopBetsMatchListComponent (
    {
        instanceId,
        sliceKey,
        disciplineId,
        limit,
        limitNumberOfEvents,
        totalRecords,
        addMore,
        redirectAnchor,
        marketsHeaderOptions,
        showMixedMatchListHeader,
        itemLayout,
        mainMarketsLimit,
        showMatchListHeader,
        showMarketInMatchListGroupHeader,
        selectGroupsOverview,
        groupOverview,
        filteredMarketGroupsOverviewList,
        filteredMarketGroupsOverview,
        isHasDrawer,
        deviceType,
    }: TopBetsMatchListProps,
) {

    return (
        <MatchList
            itemLayout={itemLayout}
            addMore={redirectAnchor ? undefined : addMore}
            addMoreAnchor={redirectAnchor}
            disciplineId={disciplineId}
            instanceId={instanceId}
            limit={limit}
            limitNumberOfEvents={limitNumberOfEvents}
            liveStatus={DisciplineLiveStatus.BOTH}
            marketsHeaderOptions={marketsHeaderOptions}
            showMixedMatchListHeader={showMixedMatchListHeader}
            sliceKey={sliceKey}
            totalRecords={totalRecords}
            mainMarketsLimit={mainMarketsLimit}
            showMatchListHeader={showMatchListHeader}
            showMarketInMatchListGroupHeader={showMarketInMatchListGroupHeader}
            selectGroupsOverview={selectGroupsOverview}
            filteredMarketGroupsOverviewList={filteredMarketGroupsOverviewList}
            filteredMarketGroupsOverview={filteredMarketGroupsOverview}
            isHasDrawer={isHasDrawer}
            deviceType={deviceType}
            groupOverview={groupOverview}
            preventRedirect={false}
        />
    );
}

const TopBetsMatchList: React.ComponentType<TopBetsMatchListPublicProps> = compose<React.ComponentType<TopBetsMatchListPublicProps>>(
    withRouting(),
    withTranslation<TopBetsMatchListPublicProps>(),
    preLoader<TopBetsMatchListPublicProps>({
        preload({ props, dispatch }) {
            const { 
                instanceId, 
                disciplineId, 
                timeRange, 
                limitNumberOfEvents, 
                groupOverview,
                locationId,
                categoryId,
                isTopBets,
                isCategory,
                timeRangeType,
                timeRangeValue,
                isAuthenForTopic,
            } = props;
            let limit: number = limitNumberOfEvents || DEFAULT_DISPLAYED_NUMBER_OF_MATCHES;
            const config: OMFE2ConfigModel = getConfig();

            if (config.virtualState) {
                const storage: MatchLimitStorageData = getVirtualStateExtraParams<MatchLimitStorageData>();
                limit = storage?.matchLimit || limit;
            }

            dispatch(getTopBetsMatchGroupsOverviewList(instanceId, disciplineId, timeRange, limit, groupOverview, locationId, categoryId, isTopBets, isCategory, timeRangeType, timeRangeValue, isAuthenForTopic));
        },
        getReadySlice({ instanceId }: TopBetsMatchListPublicProps): string {
            const slice: string = getTopBetsMatchListSliceName(instanceId);

            return `${slice}.ready`;
        },
        update({ props, prevProps, dispatch }): void {
            const { 
                instanceId, 
                disciplineId, 
                timeRange, 
                limitNumberOfEvents, 
                groupOverview,
                locationId,
                categoryId,
                isTopBets,
                isCategory,
                timeRangeType,
                timeRangeValue,
                isAuthenForTopic,
            } = props;
            let limit: number = limitNumberOfEvents || DEFAULT_DISPLAYED_NUMBER_OF_MATCHES;
            const config: OMFE2ConfigModel = getConfig();

            if (config.virtualState) {
                const storage: MatchLimitStorageData = getVirtualStateExtraParams<MatchLimitStorageData>();
                if (storage?.matchLimit) {
                    limit = limit * (storage?.matchLimit + 1) || limit;
                }
            }

            if (groupOverview !== prevProps.groupOverview || timeRange !== prevProps.timeRange || isAuthenForTopic !== prevProps.isAuthenForTopic) {
                if (difference(groupOverview.split(','), prevProps.groupOverview.split(',')).length != 0 || timeRange !== prevProps.timeRange || isAuthenForTopic !== prevProps.isAuthenForTopic) {
                    dispatch(removeTopBetsMatchGroupsOverviewList(prevProps.instanceId, timeRange !== prevProps.timeRange ? false : true));
                }
                dispatch(getTopBetsMatchGroupsOverviewList(instanceId, disciplineId, timeRange, limit, groupOverview, locationId, categoryId, isTopBets, isCategory, timeRangeType, timeRangeValue, isAuthenForTopic));
            } else if (!areTopBetsMatchListTopicPropsEqual(prevProps, props)) {
                dispatch(updateTopBetsMatchGroupsOverviewList(instanceId, disciplineId, timeRange, limit, groupOverview, locationId, categoryId, isTopBets, isCategory, timeRangeType, timeRangeValue, isAuthenForTopic));
            }
        },
        destroy({ props, dispatch }) {
            const { instanceId } = props;
            dispatch(removeTopBetsMatchGroupsOverviewList(instanceId))
        },
        loadingAnimation: MatchListSkeleton,
    }),
    connect<TopBetsMatchListStoreProps,
        TopBetsMatchListDispatchProps,
        TopBetsMatchListPublicProps>(
        (state: AppState, props: TopBetsMatchListPublicProps): TopBetsMatchListStoreProps => {
            const { instanceId } = props;
            const sliceKey: string = getTopBetsMatchListSliceName(instanceId);
            const slice: TopBetsMatchListStoreModel | undefined = state[sliceKey];

            if (!slice) {
                return {
                    sliceKey,
                    limit: 0,
                    totalRecords: 0,
                    ready: false,
                };
            }

            return {
                sliceKey,
                limit: slice.limit,
                totalRecords: slice.totalRecords,
                ready: slice.ready,
            };
        },
        {
            addMore: addMoreTopBetsMatchList,
        },
    ),
)(TopBetsMatchListComponent);
export default TopBetsMatchList;
